import store from '@/store';

export const MEDIA_TYPE = {
  WEBSITE: 'WEBSITE',
  VIDEO: 'VIDEO',
  YOUTUBE: 'YOUTUBE',
  IMAGE: 'IMAGE',
  SUBMENU: 'SUBMENU',
  SUBVODMENU: 'SUBVODMENU',
  TV: 'TV',
  RADIO: 'RADIO',
  VOD: 'VOD',
  WELCOME: 'WELCOME',
  TV_PREMIUM: 'TV_PREMIUM',
  INPUT_SOURCE: 'INPUT_SOURCE',
  SOURCE_SELECT: 'SOURCE_SELECT',
  CAST: 'CAST',
  // TV-Guide v2. MEDIA_TYPE.EPG (v1) ist hier historisch NICHT definiert und
  // damit undefined - die Weiche in navigation.handleItemCall trifft es
  // trotzdem, weil beide Seiten undefined sind. Fuer v2 wird der Typ sauber
  // gesetzt, statt sich auf diesen Zufall zu verlassen.
  EPG2: 'EPG2',
  // Adresse im EIGENEN Browser des TV oeffnen statt im iframe. Fuer schwere
  // Fremdseiten, die im iframe den Main-Thread und damit die Fernbedienung der
  // App blockieren. Siehe helpers/Browser.js.
  BROWSER: 'BROWSER',
  // Eigene Kachel fuer EINE OTT-App (Netflix & Co.) im Hauptmenue. Sie fuehrt
  // auf die Cast-Seite und von dort - nach der WLAN-Verbindung, die der Gast
  // auch fuer die Anmeldung in Netflix braucht - ohne Kachelauswahl in genau
  // diese App. Deckt den Regelfall ab: im Hauptmenue stehen eine Netflix- UND
  // eine Cast-Kachel.
  //
  // Erkannt wird am ident des Menuepunkts (ident=netflix), NICHT am Namen und
  // nicht per Teilstring in der Url - siehe ottAppKey() in navigation.js.
  OTT_APP: 'OTT_APP',
};

export const TYPE_IDENTIFIER = {
  IFRAME: '#iFrame',
  SUBMENU: '#SubMenu',
  SUBVODMENU: '#SubVodMenu',
  BASICINFOAPP: '#basicInfoApp',
  INPUT_SOURCE: '#inputsource',
  SOURCE_SELECT: '#sourceselect',
  CAST: '#cast',
  EPG2: '#epg2',
  BROWSER: '#browserApp',
  MEDIA: '#media',
};

// Endungen, an denen eine Adresse als direkte Video-Quelle erkannt wird
// (Pfad ohne Abfrage). HLS-Manifeste und gaengige Dateiformate.
const DIRECT_VIDEO_EXT = /\.(m3u8|mp4|m4v|webm|mov|mkv|ts)$/;

export default class MediaHandler {
  // Ist die Adresse eine Video-Quelle statt einer Webseite?
  static isDirectVideoUrl(url) {
    const path = String(url || '').split('?')[0].split('#')[0].toLowerCase();
    return DIRECT_VIDEO_EXT.test(path);
  }

  // Schneidet die Typ-Kennung samt fuehrendem Schraegstrich ab und liefert die
  // nackte Adresse. Ohne Kennung bleibt die Url unveraendert - ein Menuepunkt,
  // der versehentlich ohne Praefix gepflegt wurde, funktioniert dann trotzdem.
  static stripIdentifier(url, identifier) {
    const raw = String(url || '');
    const at = raw.toLowerCase().indexOf(String(identifier).toLowerCase());
    if (at === -1) return raw.trim();
    return raw.slice(at + identifier.length).replace(/^\//, '').trim();
  }


  static assembleWebsiteQuery(element, destination = '/media-player') {
    // Ein '#iFrame/' auf eine direkte Video-Adresse (z.B. ein .m3u8) gehoert in
    // den Player, nicht ins iframe: dort zeigte die TV-Engine den Stream in
    // seiner Originalgroesse klein in der Mitte (BU800, 720p auf 1080p), und
    // hls.js kam gar nicht zum Zug. Alle vier Aufrufstellen (navigation.js,
    // vod.js, Home.vue, Vod.vue) laufen hier durch.
    if (MediaHandler.isDirectVideoUrl(MediaHandler.stripIdentifier(element.url, TYPE_IDENTIFIER.IFRAME))) {
      return MediaHandler.assembleMenuVideoQuery(element, destination, TYPE_IDENTIFIER.IFRAME);
    }
    const searchParams = new URLSearchParams();
    // Die Sprache haengt hier bewusst NICHT dran. Das macht assembleUrl beim
    // Rendern des iframes (components/WebViewer.vue -> mixins/queryAssembler),
    // zusammen mit room und deviceId.
    //
    // Vorher tat es eine eigene withLang() an dieser Stelle ZUSAETZLICH - der
    // Parameter stand danach zweimal in der Url, und die hiesige Variante kuerzte
    // ihn auf zwei Zeichen: aus 'ch_de' wurde 'lang=ch', eine Sprache, die es
    // nicht gibt. Aufgefallen ist es nur bei ch_de/ch_fr/ch_it, weil alle anderen
    // i18n-Schluessel schon zweistellig sind.
    const src = element.url.replace('#iFrame/', '');

    searchParams.set('type', MEDIA_TYPE.WEBSITE);
    searchParams.set('src', src);
    return destination + '?' + searchParams.toString();
  }

  // Video aus der InfoApp-Konfiguration: Menuepunkt mit url '#media/<adresse>'.
  //
  // Bis 16.10.2026 lief so ein Eintrag durch assembleSocketVideoQuery - die
  // liest aber src_url aus einer Socket-Nutzlast, die ein Menuepunkt nicht
  // hat. Ergebnis: src leer, der Player fiel auf sein Standardvideo zurueck
  // (Fehler 3), und der Menuepunkt im Store wurde nebenbei veraendert, weil
  // die Funktion ihr Argument als Query weiterverwendet.
  //
  // Hier bekommt der Gast die Bedienung (controls), anders als bei
  // erzwungenen Einspielungen ueber den Socket. HLS (.m3u8) spielt der
  // VideoPlayer ueber hls.js.
  static assembleMenuVideoQuery(element, destination = '/media-player', identifier = TYPE_IDENTIFIER.MEDIA) {
    const src = MediaHandler.stripIdentifier(element.url, identifier);
    const lang = store.state.lang && store.state.lang.active;
    const texts = (element.lang && (element.lang[lang] || element.lang.en)) || {};
    const query = {
      type: MEDIA_TYPE.VIDEO,
      src: src,
      controls: 'true',
    };
    if (texts.title) query.title = texts.title;
    return { path: destination, query: query };
  }

  static assembleSocketVideoQuery(element, destination = '/media-player') {
    console.log('EL', element);
    const query = element;
    query.type = MEDIA_TYPE.VIDEO;
    query.src = element.src_url;
    query.controls = false;
    query.autoplay = true;
    if (element.next_action) {
      query.hasNextAction = true;
      store.dispatch('navigation/setNextAction', element.next_action);
    }
    return {
      path: destination,
      query: query,
    };
  }

  static assembleBasicInfoAppImageQuery(
    element,
    destination = '/media-player'
  ) {
    const searchParams = new URLSearchParams();
    const src = element.url.replace('#basicInfoApp/', '');

    searchParams.set('type', MEDIA_TYPE.IMAGE);
    searchParams.set('src', src);
    return destination + '?' + searchParams.toString();
  }

  static assembleSubMenuUrl(item, destination = '/sub-menu') {
    const itemArr = item.url.split('/');
    // const identifier = itemArr[0];
    // console.log(item);
    const widgetId = itemArr[1] || item.id;
    return `${destination}/${widgetId}`;
  }

  static assembleSubVodMenuUrl(item, destination = '/sub-vodmenu') {
    const itemArr = item.url.split('/');
    // const identifier = itemArr[0];
    // console.log(item);
    const widgetId = itemArr[1] || item.id;
    return `${destination}/${widgetId}`;
  }
}



// WEBPACK FOOTER //
// ./src/helpers/MediaHandler.js